import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom } from 'rxjs';
import {
  APP_TENANT_CONFIG,
  TenantConfig
} from '@bling-fe/shared/tenant-config';
import { SetCurrentTenantAction } from '../../core/states/tenant-state/tenant.actions';
import { getCurrentTenant } from '../../core/states/tenant-state/tenant.selectors';
import { Tenant } from '../../core/declarations/tenant.interface';
import { TenantRepository } from '../repositories/tenant.repository';

/**
 * Handles universal-link / app-link opens (Capacitor `appUrlOpen`).
 *
 * Deep-link URLs encode the tenant in the host (e.g. ebg.flink.coop).
 * The tenant store is synced before navigating; otherwise the
 * TenantSelectedGuard bounces to /onboarding on fresh installs or
 * cross-tenant links and the original target is lost.
 */
@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  private listening = false;

  constructor(
    private router: Router,
    private zone: NgZone,
    private store: Store,
    private tenantRepository: TenantRepository,
    @Inject(APP_TENANT_CONFIG) private tenantConfigs: TenantConfig[]
  ) {}

  init(): void {
    if (this.listening) return;
    this.listening = true;

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        const url = new URL(event.url);
        const path = `${url.pathname}${url.search}${url.hash}`;

        await this.setTenantFromDeepLinkHost(url.host);

        this.router.navigateByUrl(path);
      });
    });
  }

  private async setTenantFromDeepLinkHost(host: string): Promise<void> {
    const config = this.tenantConfigs.find(
      c => c.urls.production.includes(host) || c.urls.staging.includes(host)
    );
    if (!config) return;

    const current = await firstValueFrom(
      this.store.select(getCurrentTenant).pipe(filter(t => t !== 'loading'))
    );
    if (current && current.subdomain === config.subdomain) return;

    let tenants: Tenant[];
    try {
      tenants = await firstValueFrom(this.tenantRepository.findAll());
    } catch {
      return;
    }
    const target = tenants.find(t => t.subdomain === config.subdomain);
    if (!target) return;

    this.store.dispatch(new SetCurrentTenantAction(target));
    await firstValueFrom(
      this.store
        .select(getCurrentTenant)
        .pipe(filter(t => t !== 'loading' && t !== null && t.id === target.id))
    );
  }
}
